import type {
  AlbumInfo,
  AlbumRecord,
  CreateAlbumRequest,
  CreatePhotoRequest,
  CreatePhotoResponse,
  GalleryIndex,
  PatchAlbumRequest,
  PatchPhotoRequest,
  PhotoInfo,
  PhotoRecord,
} from '@photos/shared'
import { mediaUrl } from './media'

const TOKEN_KEY = 'photos.admin.token'

/**
 * The admin token is the whole auth story for now: whoever has the link is an
 * admin. It is kept in localStorage so a refresh doesn't lose it, and stripped
 * from the URL after first use so it stays out of the address bar.
 */
export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* private browsing — the in-memory token still works for this page load */
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken()
  const headers = new Headers(init.headers)
  if (token) headers.set('authorization', `Bearer ${token}`)
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json')

  const response = await fetch(path, { ...init, headers })
  const text = await response.text()
  const payload = text ? (JSON.parse(text) as unknown) : {}

  if (!response.ok) {
    const message = (payload as { error?: string }).error ?? response.statusText
    throw new ApiError(message, response.status)
  }
  return payload as T
}

export const admin = {
  session: () => request<{ ok: true }>('/api/admin/session'),

  list: () => request<{ photos: PhotoRecord[] }>('/api/admin/photos').then((r) => r.photos),

  create: (body: CreatePhotoRequest) =>
    request<CreatePhotoResponse>('/api/admin/photos', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  patch: (id: string, body: PatchPhotoRequest) =>
    request<{ photo: PhotoRecord }>(`/api/admin/photos/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }).then((r) => r.photo),

  remove: (id: string) => request<{ ok: true }>(`/api/admin/photos/${id}`, { method: 'DELETE' }),

  reprocess: (id: string) =>
    request<{ ok: true }>(`/api/admin/photos/${id}/reprocess`, { method: 'POST' }),

  albums: {
    list: () => request<{ albums: AlbumRecord[] }>('/api/admin/albums').then((r) => r.albums),

    create: (body: CreateAlbumRequest) =>
      request<{ album: AlbumRecord }>('/api/admin/albums', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.album),

    patch: (id: string, body: PatchAlbumRequest) =>
      request<{ album: AlbumRecord }>(`/api/admin/albums/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.album),

    remove: (id: string) =>
      request<{ ok: true }>(`/api/admin/albums/${id}`, { method: 'DELETE' }),
  },
}

/**
 * Creates the record, then sends the original straight to storage.
 *
 * In production that second step is a presigned PUT to S3 — the bytes never
 * pass through our API — and the tiling happens on the S3 event. Locally the
 * dev server takes the PUT itself. `upload.auth` says which of the two we got.
 */
export async function uploadPhoto(
  albumId: string,
  title: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<PhotoRecord> {
  const { photo, upload } = await admin.create({
    albumId,
    title,
    filename: file.name,
    contentType: file.type || 'image/jpeg',
    bytes: file.size,
  })

  const headers = { ...upload.headers }
  if (upload.auth) {
    const token = getToken()
    if (token) headers['authorization'] = `Bearer ${token}`
  }

  await put(upload.url, file, headers, onProgress)
  return photo
}

/** XHR rather than fetch, because fetch still can't report upload progress. */
function put(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    for (const [key, value] of Object.entries(headers)) xhr.setRequestHeader(key, value)

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new ApiError(`Upload failed (${xhr.status})`, xhr.status))
    }
    xhr.onerror = () => reject(new ApiError('Upload failed — network error', 0))
    xhr.send(file)
  })
}

/** The public album listing. A missing index just means nothing is published yet. */
export async function fetchIndex(): Promise<GalleryIndex> {
  const response = await fetch(mediaUrl('/media/index.json'), { cache: 'no-cache' })
  if (response.status === 404) return { updatedAt: new Date().toISOString(), albums: [] }
  if (!response.ok) throw new ApiError('Could not load the gallery', response.status)
  return (await response.json()) as GalleryIndex
}

/** One album and its photos. Absent when the album is unpublished or gone. */
export async function fetchAlbum(id: string): Promise<AlbumInfo> {
  const response = await fetch(mediaUrl(`/media/albums/${id}.json`), { cache: 'no-cache' })
  if (!response.ok) throw new ApiError('That album is not available', response.status)
  return (await response.json()) as AlbumInfo
}

export async function fetchInfo(id: string): Promise<PhotoInfo> {
  const response = await fetch(mediaUrl(`/media/${id}/info.json`), { cache: 'no-cache' })
  if (!response.ok) throw new ApiError('That photo does not exist (yet)', response.status)
  return (await response.json()) as PhotoInfo
}
